import React, { useState } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full px-4 py-2 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm';

const FILE_FIELDS = [
  { key: 'cover', label: 'Cover Image', accept: 'image/*' },
  { key: 'pdf', label: 'PDF File', accept: '.pdf,application/pdf' },
  { key: 'epub', label: 'eBook (ePub) File', accept: '.epub,application/epub+zip' },
];

const FileField = ({ field, current, file, removed, onFile, onToggleRemove }) => (
  <div>
    <label className="block font-body text-sm font-medium text-slate-700 mb-1">{field.label}</label>
    <p className="font-body text-xs text-slate-500 mb-2" data-testid={`book-current-${field.key}`}>
      {current && !removed ? 'A file is currently uploaded. Choose a new file to replace it.' : 'No file currently shown on the website.'}
    </p>
    <input
      type="file"
      accept={field.accept}
      onChange={(e) => onFile(e.target.files[0] || null)}
      className="w-full px-4 py-2 border border-slate-200 rounded font-body text-sm"
      data-testid={`book-${field.key}-input`}
    />
    {current && !file && (
      <label className="mt-2 inline-flex items-center gap-2 font-body text-xs text-red-600 cursor-pointer">
        <input type="checkbox" checked={removed} onChange={onToggleRemove} data-testid={`book-remove-${field.key}`} />
        Remove current {field.label.toLowerCase()}
      </label>
    )}
  </div>
);

export const BookEditModal = ({ book, onClose, onSaved }) => {
  const [form, setForm] = useState({ title: book.title, subtitle: book.subtitle || '', description: book.description });
  const [files, setFiles] = useState({});
  const [removed, setRemoved] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      Object.entries(files).forEach(([k, f]) => f && data.append(k, f));
      data.append('remove', Object.keys(removed).filter((k) => removed[k] && !files[k]).join(','));
      await axios.put(`${API}/admin/books/${book.id}`, data, { withCredentials: true });
      toast.success('Book updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update book');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6" data-testid="book-edit-modal">
      <div className="bg-white rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-200 flex justify-between items-center">
          <h3 className="font-heading text-xl font-medium text-brand-primary">Edit Book {String(book.order).padStart(2, '0')}</h3>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-900" data-testid="book-edit-close">
            <X className="h-6 w-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Title</label>
            <input className={inputCls} value={form.title} required onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="book-title-input" />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Subtitle (optional)</label>
            <input className={inputCls} value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="book-subtitle-input" />
          </div>
          <div>
            <label className="block font-body text-sm font-medium text-slate-700 mb-2">Description</label>
            <textarea className={`${inputCls} resize-none`} rows={5} value={form.description} required onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="book-description-input" />
          </div>
          {FILE_FIELDS.map((field) => (
            <FileField
              key={field.key}
              field={field}
              current={book[`${field.key}_url`]}
              file={files[field.key]}
              removed={!!removed[field.key]}
              onFile={(f) => setFiles({ ...files, [field.key]: f })}
              onToggleRemove={() => setRemoved({ ...removed, [field.key]: !removed[field.key] })}
            />
          ))}
          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 font-body font-medium text-slate-700 hover:text-slate-900" data-testid="book-edit-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-brand-accent text-white font-body font-medium rounded hover:bg-brand-accent-hover transition-colors duration-300 disabled:opacity-50" data-testid="save-book-button">
              {saving ? 'Saving...' : 'Save Book'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
